// @flow

import { API_ENDPOINT } from 'shared/config';
import {
  getApplicationId,
  getSessionApplicationId,
  setApplicationId
} from 'shared/utils/applicationId';
import * as errorTracking from 'shared/utils/errorTracking/errorTracking';
import { logger } from 'shared/utils/logger';
import poll from 'shared/utils/poll';

import {
  fetchWithTimeoutAndAbort,
  fetchJsonWithTimeoutAndAbort
} from './fetchWithTimeout';

export type LoanApplication = {
  loanAmount: number,
  businessStructure: string,
  companyName: string,
  email: string,
  loanTerm: number,
  uniqueCompanyIdentifier: string,
  companyCodasId: string,
  introducerId: string,
  productJourney: string
};

// should appear in the type above
export const PAYLOAD_REQUIRED_PROPERTIES = [
  'loanAmount',
  'businessStructure',
  'companyName',
  'email',
  'loanTerm',
  'uniqueCompanyIdentifier',
  'companyCodasId',
  'introducerId'
];

const SUBMIT_TIMEOUT_MS = 30000;

const FETCH_TIMEOUT_MS = 10000;

const hasEveryProperty = (payload: $Shape<LoanApplication>) =>
  PAYLOAD_REQUIRED_PROPERTIES.every((prop) =>
    Object.prototype.hasOwnProperty.call(payload, prop)
  );

export const submitApplication = (
  loanApplicationData: LoanApplication,
  timeoutMs?: number = SUBMIT_TIMEOUT_MS
) => {
  if (!hasEveryProperty(loanApplicationData)) {
    throw new Error('payload has properties missing');
  }

  const body = {
    amount_requested_cents: parseInt(loanApplicationData.loanAmount, 10) * 100,
    business_structure: loanApplicationData.businessStructure,
    company_name: loanApplicationData.companyName,
    email: loanApplicationData.email,
    term_requested_months: loanApplicationData.loanTerm,
    unique_company_identifier: loanApplicationData.uniqueCompanyIdentifier,
    company_codas_id: loanApplicationData.companyCodasId,
    introducer_id: loanApplicationData.introducerId,
    ...(loanApplicationData.productJourney && {
      product_journey: loanApplicationData.productJourney
    })
  };

  logger.log(
    `Submitting eligibility check for ${loanApplicationData.uniqueCompanyIdentifier}`
  );

  const [response] = fetchJsonWithTimeoutAndAbort(
    `${API_ENDPOINT}/eligibility_checks`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body),
      credentials: 'include'
    },
    timeoutMs
  );
  logger.log(
    `Received eligibility check response for ${loanApplicationData.uniqueCompanyIdentifier}`
  );
  return response;
};

const DEFAULT_POLL_RETRIES = 60;
const DEFAULT_POLL_INTERVAL = 2000;

export const pollNextBrokerAction = (
  applicationId: string,
  interval: number = DEFAULT_POLL_INTERVAL,
  retries: number = DEFAULT_POLL_RETRIES
) =>
  poll(
    () => getNextBrokerAction(applicationId),
    interval,
    retries,
    (action) => action.type && action.type !== 'awaiting_next_action'
  );

export const pollNextAction = (
  interval: number = DEFAULT_POLL_INTERVAL,
  retries: number = DEFAULT_POLL_RETRIES
) =>
  poll(
    getNextAction,
    interval,
    retries,
    (action) => action.type && action.type !== 'awaiting_next_action'
  );

export const NO_WAY_TO_GET_NEXT_ACTION = 'no way to get next action';

export const getNextBrokerAction = (applicationId: ?string) => {
  // application id starts out as empty string
  applicationId = applicationId || getSessionApplicationId();

  if (!applicationId) throw NO_WAY_TO_GET_NEXT_ACTION;

  logger.log(`Requesting next broker action for ${applicationId}`);

  errorTracking.setContext({ applicationId });

  const [response] = fetchJsonWithTimeoutAndAbort(
    `${API_ENDPOINT}/brokers/loan_applications/${applicationId}/next_action`,
    {
      credentials: 'include',
      cache: 'no-cache'
    },
    FETCH_TIMEOUT_MS
  );
  logger.log(`Received next broker action for ${applicationId}`);
  return response;
};

export const getNextAction = async () => {
  let applicationId = getApplicationId();

  logger.log(`Requesting next borrower action for ${applicationId}`);

  const [response] = fetchJsonWithTimeoutAndAbort(
    `${API_ENDPOINT}/loan_application/next_action`,
    {
      credentials: 'include',
      cache: 'no-cache'
    },
    FETCH_TIMEOUT_MS
  );

  logger.log(`Received next borrower action for ${applicationId}`);

  // Correctly set cookie & applicationId within error tracking if application has gone 'stale'
  if (applicationId === undefined) {
    const { application_id } = await response;
    applicationId = application_id;

    setApplicationId(applicationId);
  }

  errorTracking.setContext({ applicationId });

  return response;
};

type SubmitCurrentActionType = {
  applicationId: string,
  actionType: string,
  payload?: Object
};

export const submitCurrentAction = ({
  applicationId,
  actionType,
  payload
}: SubmitCurrentActionType) => {
  logger.log(`Submitting current action (${actionType}) for ${applicationId}`);
  const [response] = fetchWithTimeoutAndAbort(
    `${API_ENDPOINT}/loan_applications/${applicationId}/actions/${actionType}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: payload && JSON.stringify({ payload }),
      credentials: 'include'
    },
    SUBMIT_TIMEOUT_MS
  );
  logger.log(
    `Received response for current action (${actionType}) for ${applicationId}`
  );
  return response;
};
